import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { 
  Package, 
  CheckCircle2, 
  Circle, 
  Search, 
  Grid3X3,
  List,
  Plus,
  Settings,
  Layout,
  FileDown,
  ChevronDown
} from "lucide-react";
import { usePackingCases, useGearItemsPacking } from "@/hooks/usePackingGear";
import { PackingViewMode, PackingFilter, GearItemWithPacking } from "@/types/packing";
import { CreateCaseDialog } from "./CreateCaseDialog";
import { CaseManagerDialog } from "./CaseManagerDialog";
import { DragDropPackingView } from "./DragDropPackingView";
import { PackingProgressBar } from "./PackingProgressBar";
import { PackingListPDF } from "./PackingListPDF";
import { VendorPackingListPDF } from "./VendorPackingListPDF";
import { pdf } from "@react-pdf/renderer";

interface PackingModeContentProps {
  projectId: string;
  canEdit: boolean;
  projectName?: string;
}

export function PackingModeContent({ projectId, canEdit, projectName = "Project" }: PackingModeContentProps) {
  const [viewMode, setViewMode] = useState<PackingViewMode>("bins");
  const [filter, setFilter] = useState<PackingFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [caseFilter, setCaseFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<'category' | 'vendor' | 'name'>('category');
  const [showCreateCase, setShowCreateCase] = useState(false);
  const [showCaseManager, setShowCaseManager] = useState(false);

  const { cases, isLoading: isLoadingCases, deleteCase } = usePackingCases(projectId);
  const { gearItems, isLoading: isLoadingGear, togglePacked, assignToCase } = useGearItemsPacking(projectId, sortBy);

  // Get unique assigned cases for filtering
  const assignedCases = Array.from(new Set(
    gearItems?.map(item => item.assigned_case).filter(Boolean) || []
  ));

  // Filter items based on search, packed status, and case
  const filteredItems = gearItems?.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.assigned_case?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.category.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filter === "all" || 
                         (filter === "packed" && item.is_packed) ||
                         (filter === "unpacked" && !item.is_packed);
    
    const matchesCase = caseFilter === "all" || 
                       (caseFilter === "unassigned" && !item.assigned_case) ||
                       item.assigned_case === caseFilter;
    
    return matchesSearch && matchesFilter && matchesCase;
  }) || [];

  // Group items by vendor when sorting by vendor
  const itemsByVendor = sortBy === 'vendor' ? filteredItems.reduce((acc, item) => {
    const vendorKey = item.vendor_name || "No Vendor";
    if (!acc[vendorKey]) acc[vendorKey] = [];
    acc[vendorKey].push(item);
    return acc;
  }, {} as Record<string, GearItemWithPacking[]>) : {};

  // Group items by case for case view
  const itemsByCase = filteredItems.reduce((acc, item) => {
    const caseKey = item.assigned_case || "Unassigned";
    if (!acc[caseKey]) acc[caseKey] = [];
    acc[caseKey].push(item);
    return acc;
  }, {} as Record<string, GearItemWithPacking[]>);

  const handleTogglePacked = (itemId: string, currentStatus: boolean) => {
    if (!canEdit) return;
    togglePacked.mutate({ itemId, isPacked: !currentStatus });
  };

  const handleAssignToCase = (itemId: string, caseName: string | null) => {
    if (!canEdit) return;
    assignToCase.mutate({ itemId, caseName });
  };

  const handleDeleteCase = (caseId: string) => {
    if (!canEdit) return;
    deleteCase.mutate(caseId);
  };

  const handleExportPDF = async () => {
    try {
      const doc = <PackingListPDF items={gearItems || []} cases={cases || []} projectName={projectName} />;
      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${projectName}-packing-list.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleExportVendorPDF = async () => {
    try {
      const doc = <VendorPackingListPDF items={gearItems || []} projectName={projectName} />;
      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${projectName}-vendor-packing-list.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating vendor PDF:', error);
    }
  };

  const getPackedStats = () => {
    if (!gearItems) return { packed: 0, total: 0, percentage: 0 };
    const packed = gearItems.filter(item => item.is_packed).length;
    const total = gearItems.length;
    const percentage = total > 0 ? Math.round((packed / total) * 100) : 0;
    return { packed, total, percentage };
  };

  const stats = getPackedStats();

  if (isLoadingCases || isLoadingGear) {
    return <div className="flex items-center justify-center h-64">Loading gear items...</div>;
  }

  if (!gearItems || gearItems.length === 0) {
    return (
      <div className="text-center py-12">
        <Package className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">No Gear Items</h3>
        <p className="text-muted-foreground mb-6">
          Add gear items to your project first to start organizing them for packing.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with title and export controls */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl sm:text-2xl font-bold">Packing</h2>
            <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800 border-yellow-300">
              Beta
            </Badge>
          </div>
          <p className="text-sm sm:text-base text-muted-foreground">Organize your gear items into cases for transport</p>
        </div>
        
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1 text-xs md:text-sm h-8">
                <FileDown className="h-3 w-3" />
                <span className="hidden sm:inline">Export</span>
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportPDF}>
                <FileDown className="h-4 w-4 mr-2" />
                Packing List
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportVendorPDF}>
                <FileDown className="h-4 w-4 mr-2" />
                Vendor Packing List
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filters and Controls */}
      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <div className="space-y-3 sm:space-y-0 sm:flex sm:gap-4">
            {/* Search bar - full width on mobile */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search gear items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-10"
                />
              </div>
            </div>
            
            {/* Filters row - stack on mobile */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
              <Select value={filter} onValueChange={(value: PackingFilter) => setFilter(value)}>
                <SelectTrigger className="w-full sm:w-[120px] h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Items</SelectItem>
                  <SelectItem value="packed">Packed</SelectItem>
                  <SelectItem value="unpacked">Unpacked</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={caseFilter} onValueChange={setCaseFilter}>
                <SelectTrigger className="w-full sm:w-[120px] h-10">
                  <SelectValue placeholder="All Cases" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cases</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {assignedCases.map(caseName => (
                    <SelectItem key={caseName} value={caseName}>{caseName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={(value: 'category' | 'vendor' | 'name') => setSortBy(value)}>
                <SelectTrigger className="w-full sm:w-[120px] h-10">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="category">Category</SelectItem>
                  <SelectItem value="vendor">Vendor</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* View mode controls - separate row on mobile */}
          <div className="mt-3 sm:mt-4 flex justify-center sm:justify-end">
            <div className="inline-flex items-center gap-2 p-1 border rounded-lg bg-muted/20">
              <span className="text-xs font-medium text-muted-foreground px-2 hidden sm:inline">View:</span>
              <Button
                variant={viewMode === "bins" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("bins")}
                title="Drag & Drop Bins"
                className="h-8 px-3"
              >
                <Layout className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline text-xs">Bins</span>
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
                title="List View"
                className="h-8 px-3"
              >
                <List className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline text-xs">List</span>
              </Button>
              <Button
                variant={viewMode === "cases" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("cases")}
                title="Cases View"
                className="h-8 px-3"
              >
                <Grid3X3 className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline text-xs">Cases</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content based on view mode */}
      {filteredItems.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Package className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No items found</h3>
            <p className="text-muted-foreground">
              Try adjusting your filters to see more items.
            </p>
          </CardContent>
        </Card>
      ) : viewMode === "bins" ? (
        sortBy === 'vendor' && Object.keys(itemsByVendor).length > 0 ? (
          <VendorGroupedBinsView 
            itemsByVendor={itemsByVendor}
            cases={cases || []}
            canEdit={canEdit}
            onTogglePacked={handleTogglePacked}
            onAssignToCase={handleAssignToCase}
            onDeleteCase={handleDeleteCase}
            projectName={projectName}
            onShowCaseManager={() => setShowCaseManager(true)}
            onShowCreateCase={() => setShowCreateCase(true)}
          />
        ) : (
          <DragDropPackingView 
            items={filteredItems} 
            cases={cases || []}
            canEdit={canEdit} 
            onTogglePacked={handleTogglePacked} 
            onAssignToCase={handleAssignToCase}
            onDeleteCase={handleDeleteCase}
            projectName={projectName}
            onShowCaseManager={() => setShowCaseManager(true)}
            onShowCreateCase={() => setShowCreateCase(true)}
          />
        )
      ) : viewMode === "list" ? (
        sortBy === 'vendor' && Object.keys(itemsByVendor).length > 0 ? (
          <VendorGroupedListView 
            itemsByVendor={itemsByVendor}
            cases={cases || []}
            canEdit={canEdit} 
            onTogglePacked={handleTogglePacked} 
            onAssignToCase={handleAssignToCase}
            onCreateCase={() => setShowCreateCase(true)}
          />
        ) : (
          <PackingListView 
            items={filteredItems} 
            cases={cases || []}
            canEdit={canEdit} 
            onTogglePacked={handleTogglePacked} 
            onAssignToCase={handleAssignToCase}
            onCreateCase={() => setShowCreateCase(true)}
          />
        )
      ) : (
        <PackingCaseView 
          itemsByCase={itemsByCase} 
          cases={cases || []}
          canEdit={canEdit} 
          onTogglePacked={handleTogglePacked} 
          onAssignToCase={handleAssignToCase}
          onCreateCase={() => setShowCreateCase(true)}
        />
      )}

      <CreateCaseDialog
        projectId={projectId}
        open={showCreateCase}
        onOpenChange={setShowCreateCase}
      />

      <CaseManagerDialog
        projectId={projectId}
        open={showCaseManager}
        onOpenChange={setShowCaseManager}
      />
    </div>
  );
}

function PackingListView({ 
  items, 
  cases,
  canEdit, 
  onTogglePacked, 
  onAssignToCase,
  onCreateCase
}: {
  items: GearItemWithPacking[];
  cases: any[];
  canEdit: boolean;
  onTogglePacked: (itemId: string, currentStatus: boolean) => void;
  onAssignToCase: (itemId: string, caseName: string | null) => void;
  onCreateCase: () => void;
}) {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="divide-y">
          {items.map((item) => (
            <div key={item.id} className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <button
                  onClick={() => onTogglePacked(item.id, item.is_packed)}
                  disabled={!canEdit}
                  className="flex-shrink-0 p-1"
                >
                  {item.is_packed ? (
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  ) : (
                    <Circle className="h-6 w-6 text-muted-foreground hover:text-green-600" />
                  )}
                </button>
                
                <div className="flex-1 min-w-0">
                  <h4 className={`font-medium text-sm sm:text-base ${item.is_packed ? 'line-through text-muted-foreground' : ''}`}>
                    {item.name}
                  </h4>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground mt-1">
                    <span>Qty: {item.quantity}</span>
                    <Badge variant="outline" className="text-xs">{item.category}</Badge>
                    {item.vendor_name && <span>Vendor: {item.vendor_name}</span>}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2 sm:ml-auto">
                <Select 
                  value={item.assigned_case || "none"} 
                  onValueChange={(value) => {
                    if (value === "create_new") {
                      onCreateCase();
                    } else {
                      onAssignToCase(item.id, value === "none" ? null : value);
                    }
                  }}
                  disabled={!canEdit}
                >
                  <SelectTrigger className="w-full sm:w-40 h-9">
                    <SelectValue placeholder="Assign case..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No case</SelectItem>
                    {cases.map(caseItem => (
                      <SelectItem key={caseItem.id} value={caseItem.name}>
                        {caseItem.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="create_new" className="text-primary font-medium">
                      <Plus className="h-4 w-4 mr-2" />
                      Create new case...
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function PackingCaseView({ 
  itemsByCase, 
  cases,
  canEdit, 
  onTogglePacked, 
  onAssignToCase,
  onCreateCase
}: {
  itemsByCase: Record<string, GearItemWithPacking[]>;
  cases: any[];
  canEdit: boolean;
  onTogglePacked: (itemId: string, currentStatus: boolean) => void;
  onAssignToCase: (itemId: string, caseName: string | null) => void;
  onCreateCase: () => void;
}) {
  // Get case color for display
  const getCaseColor = (caseName: string) => {
    const caseObj = cases.find(c => c.name === caseName);
    return caseObj?.color || '#6b7280';
  };

  return (
    <div className="space-y-4">
      {Object.entries(itemsByCase).map(([caseName, caseItems]) => {
        const packedCount = caseItems.filter(item => item.is_packed).length;
        const totalCount = caseItems.length;
        const percentage = Math.round((packedCount / totalCount) * 100);
        const caseColor = getCaseColor(caseName);
        
        return (
          <Card key={caseName}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded" 
                    style={{ backgroundColor: caseName === "Unassigned" ? '#6b7280' : caseColor }}
                  />
                  {caseName}
                </CardTitle>
                <Badge variant={percentage === 100 ? "default" : "secondary"}>
                  {packedCount}/{totalCount} ({percentage}%)
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {caseItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 p-2 rounded border">
                    <button
                      onClick={() => onTogglePacked(item.id, item.is_packed)}
                      disabled={!canEdit}
                      className="flex-shrink-0"
                    >
                      {item.is_packed ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <Circle className="h-5 w-5 text-muted-foreground hover:text-green-600" />
                      )}
                    </button>
                    
                    <div className="flex-1">
                      <span className={`font-medium ${item.is_packed ? 'line-through text-muted-foreground' : ''}`}>
                        {item.name}
                      </span>
                      <div className="text-sm text-muted-foreground">
                        Qty: {item.quantity} • {item.category}
                        {item.vendor_name && ` • ${item.vendor_name}`}
                      </div>
                    </div>

                    {canEdit && (
                      <Select 
                        value={item.assigned_case || "none"} 
                        onValueChange={(value) => {
                          if (value === "create_new") {
                            onCreateCase();
                          } else {
                            onAssignToCase(item.id, value === "none" ? null : value);
                          }
                        }}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No case</SelectItem>
                          {cases.map(caseItem => (
                            <SelectItem key={caseItem.id} value={caseItem.name}>
                              {caseItem.name}
                            </SelectItem>
                          ))}
                          <SelectItem value="create_new" className="text-primary font-medium">
                            <Plus className="h-4 w-4 mr-2" />
                            Create new case...
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// Vendor Grouped Bins View Component
function VendorGroupedBinsView({ 
  itemsByVendor, 
  cases,
  canEdit, 
  onTogglePacked, 
  onAssignToCase,
  onDeleteCase,
  projectName,
  onShowCaseManager,
  onShowCreateCase
}: {
  itemsByVendor: Record<string, GearItemWithPacking[]>;
  cases: any[];
  canEdit: boolean;
  onTogglePacked: (itemId: string, currentStatus: boolean) => void;
  onAssignToCase: (itemId: string, caseName: string | null) => void;
  onDeleteCase: (caseId: string) => void;
  projectName: string;
  onShowCaseManager: () => void;
  onShowCreateCase: () => void;
}) {
  const totalItems = Object.values(itemsByVendor).flat().length;
  const packedItems = Object.values(itemsByVendor).flat().filter(item => item.is_packed).length;

  return (
    <div className="space-y-6">
      {/* Progress and controls section */}
      <div className="sticky top-2 sm:top-4 z-10 flex flex-col gap-3 sm:gap-4">
        <PackingProgressBar 
          packedCount={packedItems}
          totalCount={totalItems}
        />
        
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center sm:justify-between items-center">
          {canEdit && (
            <div className="flex gap-2 w-full sm:w-auto">
              <Button 
                variant="outline" 
                onClick={onShowCaseManager}
                size="sm"
                className="flex-1 sm:flex-none"
              >
                <Settings className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Manage Cases</span>
                <span className="sm:hidden">Manage</span>
              </Button>
              <Button 
                onClick={onShowCreateCase}
                size="sm"
                className="flex-1 sm:flex-none"
              >
                <Plus className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Create Case</span>
                <span className="sm:hidden">Create</span>
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Vendor sections */}
      {Object.entries(itemsByVendor).map(([vendorName, vendorItems]) => (
        <Card key={vendorName} className="overflow-hidden">
          <CardHeader className="bg-muted/20 pb-3">
            <CardTitle className="flex items-center gap-3 text-lg">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-primary" />
                <span>{vendorName}</span>
              </div>
              <Badge variant="secondary" className="ml-auto">
                {vendorItems.length} items
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <DragDropPackingView 
              items={vendorItems} 
              cases={cases}
              canEdit={canEdit} 
              onTogglePacked={onTogglePacked} 
              onAssignToCase={onAssignToCase}
              onDeleteCase={onDeleteCase}
              projectName={projectName}
              onShowCaseManager={onShowCaseManager}
              onShowCreateCase={onShowCreateCase}
            />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Vendor Grouped List View Component
function VendorGroupedListView({ 
  itemsByVendor, 
  cases,
  canEdit, 
  onTogglePacked, 
  onAssignToCase,
  onCreateCase
}: {
  itemsByVendor: Record<string, GearItemWithPacking[]>;
  cases: any[];
  canEdit: boolean;
  onTogglePacked: (itemId: string, currentStatus: boolean) => void;
  onAssignToCase: (itemId: string, caseName: string | null) => void;
  onCreateCase: () => void;
}) {
  return (
    <div className="space-y-4">
      {Object.entries(itemsByVendor).map(([vendorName, vendorItems]) => (
        <Card key={vendorName} className="overflow-hidden">
          <CardHeader className="bg-muted/20 pb-3">
            <CardTitle className="flex items-center gap-3 text-lg">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-primary" />
                <span>{vendorName}</span>
              </div>
              <Badge variant="secondary" className="ml-auto">
                {vendorItems.length} items
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {vendorItems.map((item) => (
                <div key={item.id} className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <button
                      onClick={() => onTogglePacked(item.id, item.is_packed)}
                      disabled={!canEdit}
                      className="flex-shrink-0 p-1"
                    >
                      {item.is_packed ? (
                        <CheckCircle2 className="h-6 w-6 text-green-600" />
                      ) : (
                        <Circle className="h-6 w-6 text-muted-foreground hover:text-green-600" />
                      )}
                    </button>
                    
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-medium text-sm sm:text-base ${item.is_packed ? 'line-through text-muted-foreground' : ''}`}>
                        {item.name}
                      </h4>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground mt-1">
                        <span>Qty: {item.quantity}</span>
                        <Badge variant="outline" className="text-xs">{item.category}</Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 sm:ml-auto">
                    <Select 
                      value={item.assigned_case || "none"} 
                      onValueChange={(value) => {
                        if (value === "create_new") {
                          onCreateCase();
                        } else {
                          onAssignToCase(item.id, value === "none" ? null : value);
                        }
                      }}
                      disabled={!canEdit}
                    >
                      <SelectTrigger className="w-full sm:w-40 h-9">
                        <SelectValue placeholder="Assign case..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No case</SelectItem>
                        {cases.map(caseItem => (
                          <SelectItem key={caseItem.id} value={caseItem.name}>
                            {caseItem.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="create_new" className="text-primary font-medium">
                          <Plus className="h-4 w-4 mr-2" />
                          Create new case...
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}